import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { InformationalScreen, LoadingModal, Text } from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import {
  Footer,
  FormContainer,
  Input,
  MainContainer,
  PhoneInput,
  TopHeaderBackArrow,
} from 'Components/utility';
import { ApiStatus, ErrorCode } from 'Constants';
import { type FormikErrors, type FormikHelpers, type FormikValues, useFormik } from 'formik';
import { useApiError } from 'Hooks';
import page from 'page';
import { verifyEmailOrPhone } from 'Services/verify';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

type MyFormValues = {
  email: string;
  phone: string;
};

const initialValues: MyFormValues = { email: '', phone: '' };

const mapStateToProps = ({
  nav: { query },
  env: { deviceId, osType, osVersion, app, appVersion },
}: RootState) => ({
  query,
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const SignIn = ({ deviceId, osType, osVersion, app, appVersion, query }: Props) => {
  const validationSchema = z
    .object({
      phone: z
        .string()
        .trim()
        .default('')
        .refine(
          (value) => {
            // Don't verify phone number if empty
            if (!value) {
              return true;
            }

            return parsePhoneNumberFromString(value)?.isPossible() ?? false;
          },
          { error: i18n.t('SignIn.content.signInForm.formField.phone.errors.phoneNumberValid') }
        )
        .optional(),
      email: z
        .string()
        .email({
          error: i18n.t('SignIn.content.signInForm.formField.email.errors.emailAddressValidEmail'),
        })
        .default('')
        .optional(),
    })
    .refine(
      (data) => {
        if (data.email !== '') {
          return true;
        }

        return !(!!data.email && data.phone === '');
      },
      {
        error: i18n.t('SignIn.content.signInForm.formField.email.errors.required'),
        path: ['email'],
      }
    )
    .refine(
      (data) => {
        if (data.phone !== '') {
          return true;
        }

        return !(data.email === '' && !!data.phone);
      },
      {
        error: i18n.t('SignIn.content.signInForm.formField.phone.errors.required'),
        path: ['phone'],
      }
    );

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setErrors: (errors: FormikErrors<FormikValues>) => void
    ) => {
      const data = error.response?.data ?? {
        details:
          error.code === ErrorCode.NETWORK_ERROR
            ? ErrorCode.NETWORK_ERROR
            : i18n.t('Global.modals.errorCommunicatingServer.body'),
      };

      notifyApp('adc-webview:verify-account', { status: error.status, data });
      const code = error.response?.data.code;

      switch (error.status) {
        case ApiStatus.BAD_REQUEST: {
          showApiErrorModal();
          break;
        }

        case ApiStatus.OK: {
          if (code === ApiStatus.EMAIL_NOT_FOUND) {
            setErrors({
              email: i18n.t('SignIn.content.apiResponseErrors.notFound'),
            });

            return;
          }

          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }

          resetForm();
        }
      }
    },
    [showApiErrorModal, showNetworkErrorModal]
  );

  const signInVerifyRequest = useCallback(
    async (
      { email, phone }: MyFormValues,
      resetForm: () => void,
      setErrors: (errors: FormikErrors<FormikValues>) => void,
      setSubmitting: (isSubmitting: boolean) => void,
      force = false
    ) => {
      setSubmitting(true);

      try {
        const { status, data } = await verifyEmailOrPhone(
          email,
          phone,
          osType,
          osVersion,
          app,
          appVersion,
          deviceId,
          force
        );

        if (status === ApiStatus.OK) {
          switch (data?.code) {
            case ApiStatus.EMAIL_FOUND_ACTIVATED: {
              const queryParams = new URLSearchParams(
                Object.entries(query).map(([key, value]) => [key, String(value)])
              );

              queryParams.set('email', email);
              const queryString = queryParams.toString();
              const url = `/login?${queryString}`;

              mediator.publish('router:redirect', url);
              page.exit(url, function (_ctx, next) {
                setSubmitting(false);
                next();
              });
              break;
            }

            case ApiStatus.EMAIL_FOUND_NOT_ACTIVATED:
              mediator.publish('router:redirect', '/verify-account');
              page.exit('/password-setup', function (_ctx, next) {
                setSubmitting(false);
                next();
              });
              break;
            case ApiStatus.EMAIL_NOT_FOUND:
              setSubmitting(false);
              setErrors({
                email: i18n.t('SignIn.content.apiResponseErrors.notFound'),
              });
              break;
            case ApiStatus.PHONE_NOT_FOUND:
              setSubmitting(false);
              setErrors({
                phone: i18n.t('SignIn.content.apiResponseErrors.notFound'),
              });
              break;
          }
        }

        notifyApp('adc-webview:verify-account', data);
      } catch (error) {
        setSubmitting(false);
        handleApiRequestError(error as AxiosError<ApiErrorData>, resetForm, setErrors);
      }
    },
    [app, appVersion, deviceId, handleApiRequestError, osType, osVersion, query]
  );

  const { handleSubmit, handleChange, handleBlur, isValid, errors, touched, values, isSubmitting } =
    useFormik({
      initialValues,
      validationSchema: toFormikValidationSchema(validationSchema),
      onSubmit: async (
        values: MyFormValues,
        { setSubmitting, resetForm, setErrors }: FormikHelpers<MyFormValues>
      ) => {
        await signInVerifyRequest(values, resetForm, setErrors, setSubmitting);
      },
      validateOnBlur: true,
      initialErrors: {
        email: i18n.t('SignIn.content.signInForm.formField.email.errors.emailAddressValidEmail'),
      },
    });

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}
      <InformationalScreen
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('SignIn.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="Auth.action-go-back" />,
            label: i18n.t('SignIn.title'),
            onPress: () => {
              notifyApp('adc-webview:navigate-back-app');
            },
          },
          testID: 'Auth.actionSignInTitle',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('SignIn.subtitle')}</Text>
            <FormContainer>
              <Input
                aria-label={i18n.t('SignIn.content.signInForm.formField.email.label')}
                placeholder={i18n.t('SignIn.content.signInForm.formField.email.placeholder')}
                label={i18n.t('SignIn.content.signInForm.formField.email.label')}
                errorMessage={errors.email}
                isInvalid={Boolean(errors.email && touched.email)}
                value={values.email}
                testID="email"
                onChangeText={handleChange('email')}
                onBlur={handleBlur('email')}
                inputMode="email"
                disabled={!!values.phone}
              />
              <Text marginTop={-8}>
                <strong>OR</strong>
              </Text>
              <PhoneInput
                aria-label={i18n.t('SignIn.content.signInForm.formField.phone.label')}
                placeholder={i18n.t('SignIn.content.signInForm.formField.phone.placeholder')}
                label={i18n.t('SignIn.content.signInForm.formField.phone.label')}
                errorMessage={errors.phone}
                isInvalid={Boolean(errors.phone && touched.phone)}
                value={values.phone}
                testID="phoneNumber"
                onChangeText={handleChange('phone')}
                onBlur={handleBlur('phone')}
                disabled={!!values.email}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t('SignIn.content.footerCreateAccount.links.signIn.label')}
              onButtonPress={handleSubmit}
              buttonTestID="SignIn.submit-btn"
              disabled={!isValid}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(SignIn);
